import { Card, Grid, CardContent, Typography, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { typoBase, smallBold, thinBold } from "../styles/typography";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getTimesheetsByCaseId } from "../redux/actions/timesheetActions";
import { getSalesPaymentsByCaseId } from "../redux/actions/salesPaymentActions";
import {
  Avatar,
  Table,
  TableBody,
  TableCell,
  Box,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from "../styles/typography";
import { formatDate } from "../utils/dateUtils";

const NotesCard = ({ notes, handleOpenNotes, fileResponsibleLawyer, file, onDeleteNote }) => {
  const { t } = useTranslation(['common', 'buttons']);
  const { timesheets, loading, error } = useSelector(
    (state) => state.timesheetList
  );
  const { salesPayments } = useSelector((state) => state.salesPaymentList);
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();
  const [hoveredNoteId, setHoveredNoteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    dispatch(getTimesheetsByCaseId(id)); // Fetch timesheets based on the case ID
    dispatch(getSalesPaymentsByCaseId(id));
  }, [dispatch, id]);


  const formatDateTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const formattedDate = formatDate(date);

    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    return `${formattedDate}, ${formattedTime}`;
  };

  const handleDeleteClick = (noteId, e) => {
    e.stopPropagation();
    setNoteToDelete(noteId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (noteToDelete && onDeleteNote) {
      try {
        await onDeleteNote(noteToDelete);
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  return (
    <Card
      sx={{
        boxShadow: "none",
        border: "none",
        marginTop: 2,
        overflowY: "auto",
        maxHeight: "450px",
        "&::-webkit-scrollbar": {
          width: "5px", // Set the width of the scrollbar
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "lightgrey", // Set the color of the scrollbar thumb
          borderRadius: "0", // Set the border radius of the scrollbar thumb
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#888", // Set the color of the scrollbar thumb on hover
        },
      }}
    >
      <CardContent
        sx={{
          padding: 0,
          paddingRight: 0.7,
          margin: 0,
        }}
      >
        {notes.length > 0 ? (
          <Box sx={{ border: "1px solid #d1d1d1" }}>
            <Box>
              {notes.map((note, index) => (
                <Box
                  key={note._id}
                  onMouseEnter={() => setHoveredNoteId(note._id)}
                  onMouseLeave={() => setHoveredNoteId(null)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#ecf5fb",
                    },
                    padding: 1,
                    borderBottom:
                      index === notes.length - 1 ? "none" : "1px solid #d1d1d1",
                    overflow: "hidden",
                    wordWrap: "break-word",
                  }}
                >
                  <Box
                    onClick={() => handleOpenNotes()}
                    sx={{ flex: 1 }}
                >
                  <Box
                    sx={{ 
                      display: "flex", 
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                      marginBottom: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Avatar
                        alt={note.author?.firstName || 'Unknown'}
                        src={
                          note.author?.avatar
                            ? note.author.avatar
                            : "/static/images/avatar/1.jpg"
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                        }}
                      />
                      <Box
                        sx={{
                          color: "rgb(53, 53, 53)",
                          display: "block",
                          fontFamily:
                            '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                          fontSize: "10.5px",
                          fontWeight: 400,
                          lineHeight: "1.2",
                          maxWidth: "120px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {note.author
                          ? `${note.author.firstName || ''} ${note.author.lastName || ''}`.trim() || note.author.businessName || t('common:fallbacks.notAvailable')
                          : t('common:fallbacks.notAvailable')
                        }
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        color: "rgb(117, 117, 117)",
                        display: "block",
                        fontFamily:
                          '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                        fontSize: "9px",
                        fontWeight: 400,
                        lineHeight: "1.2",
                        flexShrink: 0,
                        minWidth: "fit-content",
                      }}
                    >
                        <Typography
                        variant="caption"
                        sx={{
                          ...thinBold,
                          color: "#777",
                          fontSize: "10px",
                        }}
                        >
                          {formatDateTime(note.date)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      color: "rgb(35, 35, 35)",
                      display: "block",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "10.5px",
                      fontWeight: 400,
                      lineHeight: "1.4",
                      paddingTop: 1,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {note.content.length > 80
                      ? `${note.content.substring(0, 80)}...`
                      : note.content}
                  </Box>
                  </Box>
                  {hoveredNoteId === note._id && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(note._id, e)}
                      sx={{
                        color: '#666',
                        '&:hover': {
                          color: '#d32f2f',
                        },
                        marginLeft: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={2}>
            <Typography variant="body1" style={{ color: '#777' }}>
              {t('no_data_available')}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('common:confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common:confirm_delete_note')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:secondary.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('buttons:danger.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default NotesCard;